import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations, { LANGUAGES } from '../translations';

// Create the Language Context
const LanguageContext = createContext();

// Language Provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language or detect browser language
    const saved = localStorage.getItem('chessKingdomLanguage');
    if (saved && translations[saved]) {
      return saved;
    }
    // Try to detect browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) {
      return browserLang;
    }
    return 'en';
  });

  const [currentTranslations, setCurrentTranslations] = useState(translations[language] || translations.en);

  // Update translations when language changes
  useEffect(() => {
    setCurrentTranslations(translations[language] || translations.en);
    localStorage.setItem('chessKingdomLanguage', language);
    
    // Update document direction for RTL languages
    const langInfo = LANGUAGES.find(l => l.code === language);
    document.documentElement.dir = langInfo?.dir || 'ltr';
    document.documentElement.lang = language;
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
  }, [language]);

  // Listen for language changes from other components (like CoverScreen)
  useEffect(() => {
    const handleLanguageChange = (event) => {
      if (event.detail && translations[event.detail]) {
        setLanguage(event.detail);
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Translation function with dot notation support
  const t = useCallback((key, fallback = '') => {
    if (!key) return fallback;
    
    const keys = key.split('.');
    let result = currentTranslations;
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        // Fallback to English if key not found
        result = translations.en;
        for (const k2 of keys) {
          if (result && typeof result === 'object' && k2 in result) {
            result = result[k2];
          } else {
            return fallback || key;
          }
        }
        break;
      }
    }
    
    return typeof result === 'string' ? result : (fallback || key);
  }, [currentTranslations]);

  // Get current language info
  const getCurrentLanguage = useCallback(() => {
    return LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    translations: currentTranslations,
    languages: LANGUAGES,
    getCurrentLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use translations
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

// Export the context for direct access if needed
export default LanguageContext;
